import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import apiUrl from "../../Apiurl";
import { normalizeColumnOrder } from "./columnOrder";

const VERSION = 2;
const EMPTY_COLUMNS = {};
const EMPTY_IDS = [];

export default function useColumnPreferences(accountKey, tableKey, viewKey, columnIds, lockedIds = EMPTY_IDS) {
  const { getAccessTokenSilently } = useAuth0();
  const [loaded, setLoaded] = useState(null);
  const [status, setStatus] = useState("loading");
  const scopeKey = JSON.stringify([accountKey, tableKey, viewKey]);
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;
  const configurationRef = useRef(null);
  const pendingRef = useRef(null);
  const timerRef = useRef(null);
  const chainRef = useRef(Promise.resolve());
  const revisionRef = useRef(0);
  const endpoint = `${apiUrl}/auth/preferenze/colonne/${tableKey}/${viewKey}?version=2`;

  const enqueue = useCallback((operation) => {
    chainRef.current = chainRef.current.catch(() => {}).then(async () => {
      if (scopeRef.current !== operation.scopeKey) return;
      const token = await getAccessTokenSilently();
      if (scopeRef.current !== operation.scopeKey) return;
      const response = await fetch(endpoint, {
        method: operation.method,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(operation.body ? { "Content-Type": "application/json" } : {}),
        },
        ...(operation.body ? { body: JSON.stringify(operation.body) } : {}),
      });
      if (!response.ok) throw new Error("Preferenza colonne non salvata");
      if (scopeRef.current === operation.scopeKey && revisionRef.current === operation.revision) {
        setStatus("saved");
      }
    }).catch(() => {
      if (scopeRef.current === operation.scopeKey && revisionRef.current === operation.revision) {
        setStatus("error");
      }
    });
  }, [endpoint, getAccessTokenSilently]);

  const flush = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (pendingRef.current) {
      enqueue(pendingRef.current);
      pendingRef.current = null;
    }
  }, [enqueue]);

  useEffect(() => {
    configurationRef.current = null;
    revisionRef.current += 1;
    pendingRef.current = null;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setLoaded(null);
    setStatus("loading");
    if (!accountKey) return undefined;
    const controller = new AbortController();
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error("Preferenza colonne non disponibile");
        const preference = await response.json();
        if (![1, VERSION].includes(preference.version) || !preference.columns ||
            typeof preference.columns !== "object" || Array.isArray(preference.columns) ||
            (preference.version === VERSION && !Array.isArray(preference.order))) {
          throw new Error("Versione preferenza colonne non supportata");
        }
        if (!controller.signal.aborted && scopeRef.current === scopeKey) {
          const validIds = new Set(columnIds);
          const columns = Object.fromEntries(Object.entries(preference.columns)
            .filter(([id, visible]) => validIds.has(id) && typeof visible === "boolean"));
          lockedIds.forEach((id) => { columns[id] = true; });
          const order = normalizeColumnOrder(preference.order, columnIds, lockedIds);
          const next = { scopeKey, columns, order };
          configurationRef.current = next;
          setLoaded(next);
          setStatus("saved");
        }
      } catch (error) {
        if (!controller.signal.aborted && scopeRef.current === scopeKey) setStatus("loadError");
      }
    })();
    return () => controller.abort();
  }, [accountKey, scopeKey, columnIds, lockedIds, endpoint, getAccessTokenSilently]);

  useEffect(() => () => flush(), [flush]);

  const columns = loaded?.scopeKey === scopeKey ? loaded.columns : EMPTY_COLUMNS;
  const order = loaded?.scopeKey === scopeKey ? loaded.order : columnIds;
  const ready = loaded?.scopeKey === scopeKey;

  // Spunte, frecce e trascinamento accodano lo stesso documento. Il ref evita
  // che due azioni ravvicinate ripartano da una configurazione precedente.
  const update = useCallback((transform) => {
    const current = configurationRef.current;
    if (current?.scopeKey !== scopeKey) return;
    const next = { ...transform(current), scopeKey };
    configurationRef.current = next;
    setLoaded(next);
    setStatus("saving");
    const revision = ++revisionRef.current;
    pendingRef.current = {
      scopeKey,
      revision,
      method: "PATCH",
      body: { version: VERSION, columns: next.columns, order: next.order },
    };
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 500);
  }, [scopeKey, flush]);

  const change = useCallback((id, visible) => {
    if (!columnIds.includes(id) || lockedIds.includes(id)) return;
    update((current) => ({ ...current, columns: { ...current.columns, [id]: visible } }));
  }, [columnIds, lockedIds, update]);

  const reorder = useCallback((nextOrder) => {
    update((current) => ({ ...current, order: normalizeColumnOrder(nextOrder, columnIds, lockedIds) }));
  }, [columnIds, lockedIds, update]);

  const reset = useCallback(() => {
    if (!ready) return;
    flush();
    const next = { scopeKey, columns: {}, order: normalizeColumnOrder([], columnIds, lockedIds) };
    configurationRef.current = next;
    setLoaded(next);
    setStatus("saving");
    enqueue({ scopeKey, revision: ++revisionRef.current, method: "DELETE" });
  }, [scopeKey, columnIds, lockedIds, enqueue, flush, ready]);

  return { columns, order, ready, status, change, reorder, reset };
}
